import { useAPIStore } from '@/stores/api'
import { useBrandingStore } from '@/stores/branding'
import { useComponentsStore } from '@/stores/components'
import { useGenericStore } from '@/stores/generic'
import { usePageStore } from '@/stores/page'
import { usePersonsStore } from '@/stores/persons'
import { useProductsStore } from '@/stores/products'
import { useProtocolStore } from '@/stores/protocol'
import { useScreenshotsStore } from '@/stores/screenshots'
import { toRaw } from 'vue'
import { forEachObject, deepValue, clone } from '@broker-as/acquisit-ui-vue/functions'
import { CycleFinder, useLogger } from '@broker-as/acquisit-ui-vue/library'
import { useLogsStore } from '@/stores/log'

const log = useLogger('stores/backup', useLogger.COLOR_STORE)

export interface Backup {
	build: number
	api: Record<string, any>
	branding: Record<string, any>
	components: Record<string, any>
	generic: Record<string, any>
	page: Record<string, any>
	persons: Record<string, any>
	products: Record<string, any>
	protocol: Record<string, any>
	screenshots: Record<string, any>
}

export const useBackup = () => {
	const STORES = {
		api: useAPIStore(),
		branding: useBrandingStore(),
		generic: useGenericStore(),
		page: usePageStore(),
		persons: usePersonsStore(),
		products: useProductsStore(), // Products need to be restored before protocol to link them properly
		protocol: useProtocolStore(),
		components: useComponentsStore(),
		screenshots: useScreenshotsStore(),
		logs: useLogsStore()
	}
	
	const createForStore = store => {
		if (store.backup) {
			return clone(toRaw(store.backup))
		} else {
			return clone(toRaw(store.$state))
		}
	}

	const create = (): Backup => {
		let backup: Record<string, any> = {
			build: import.meta.env.BUILD_NUMERIC
		}
		
		forEachObject(STORES, (store, key) => {
			backup[key] = createForStore(store)
		})
		
		if (window.DEBUG) {
			const cycles = new CycleFinder().findCycles(backup, 'backup')
			
			if (cycles.length) {
				console.warn('Cycles found:', cycles)
				
				for (let key of cycles) {
					let actualKey = key.substring('backup.'.length).replace(/\[(\d+)]/g, '.$1'),
						actualValue = deepValue(backup, actualKey)
					
					//log.debugFrom('createBackup', actualKey, actualValue)
				}
			}
		}
		
		return backup as Backup
	}

	const restore = (backup: Backup) => {
		try {
			forEachObject(STORES, (store, key) => {
				if (backup[key]) {
					if (store.restoreBackup) {
						store.restoreBackup(backup[key], backup)
					} else {
						store.$state = backup[key]
					}
				}
			})
			
			useLogsStore().event('Restored from backup')
		} catch (e) {
			log.always.errorFrom('useBackup:restore', 'Could not restore backup', e)
			log.debugFrom('useBackup:restore', backup)
			
			useLogsStore().error('Restore from backup failed', e)
			
			forEachObject(STORES, (store) => {
				store.$reset()
			})
			
			throw e
		}
	}
	
	return {
		STORES,
		create,
		createForStore,
		restore
	}
}